import { getAssignmentsClient, getRailsClient } from "sj-ui-toolkit/src/client";
import {
  API_ASSIGNMENT_POLICIES_PATH,
  apiAssignmentPath,
} from "sj-ui-toolkit/src/constants/routes";
import { normalizeNotificationsFrequency } from "@/helpers/assignmentHelpers";
import type { KaStatusKey } from "sj-ui-toolkit/src/constants/assessmentStatus";

// ─── Shared types ─────────────────────────────────────────────────────────────

export interface DirectoryUserShape {
  directoryUuid?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  name?: {
    givenName?: string;
    familyName?: string;
  };
  archived?: boolean;
  licensed?: boolean;
  status?: KaStatusKey;
  attributes?: Record<string, unknown>;
  developerProfile?:
    | {
        topics?: string[];
        language?: string;
        experience?: string;
        expertise?: string;
        jobFunction?: string;
        aiStack?: Record<string, string[] | undefined>;
        usesAi?: boolean;
        additionalLanguages?: string[];
      }
    | boolean;
  averageScore?: number | null;
  proficiency?: string | null;
}

export interface StoredUser {
  directoryUuid?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  name?: {
    givenName?: string;
    familyName?: string;
  };
  assignmentId?: string;
}

export interface Path {
  id?: string;
  name?: string;
  title?: string;
  description?: string;
  type?: string;
  status?: string;
  versions?: Path[];
  duration?: number;
  roleId?: string;
  levelName?: string;
  lessonsCount?: number;
  totalDuration?: number;
  archived?: boolean;
  activated?: boolean;
  lessons?: Array<{ durationNumber?: number }>;
  microRoles?: Array<{
    lessons?: Array<{ durationNumber?: number }>;
  }>;
}

export interface PathsApiResponse {
  defaultPaths?: Path[];
  paths?: Path[];
  quests?: Path[];
}

export interface Level {
  id?: number;
  name?: string;
  rankName?: string;
}

export interface AssignedUser {
  id?: string;
  user?: {
    email?: string;
    firstName?: string;
    lastName?: string;
    directoryUuid?: string;
  };
}

export interface AssignmentPolicyRule {
  id?: string;
  level?: string;
  user_level?: string;
}

export interface CreateAssignmentPayload {
  name: string;
  description: string;
  activated: boolean;
  dueDateAt: number | null;
  notificationsFrequency: string;
  roleId?: string;
  locale?: string;
  duration?: number;
  enrollmentDelay?: number;
}

export interface NewHireRulePayload {
  user_level: string;
  audience: null;
  company: null;
  country: null;
  department: null;
  managers_email: null;
  manager: null;
  security_champion: null;
  team: null;
  title: null;
  job_role: null;
}

export interface FormSettings {
  name?: string;
  description?: string;
  activated?: boolean;
  sendReminders?: string;
  endDate?: Date;
  dates?: {
    dynamic?: boolean;
    dueAfter?: { days: number };
  };
  strict?: boolean;
  isRequired?: boolean;
}

export interface FormValues {
  selectedPath: Path | null;
  target: {
    availableUsers?: DirectoryUserShape[];
    allUsers?: Map<string, { archived: boolean; licensed: boolean }>;
    users?: StoredUser[];
  };
  settings: FormSettings | Record<string, unknown>;
}

// ─── URL constants (private to this module) ──────────────────────────────────

const API_V1 = "/api/internal_api/v1/admin";
const ATLAS_API_V1 = "/api/internal_api/atlas/v1/admin";

// Types
export interface AssignmentData {
  id?: string;
  name?: string;
  dueDateAt?: string;
  activated?: boolean;
  updatedAt?: string;
  progress?: Record<string, unknown>;
}

export interface AssignmentPolicyData {
  id?: string;
  name?: string;
  dueDateAt?: string;
  activated?: boolean;
  updatedAt?: string;
  audiences?: Array<{ name?: string }>;
  progress?: Record<string, unknown>;
}

// Constants
const PROCESSING_ASSIGNMENTS_KEY = "processingAssignmentIds";
const PROCESSING_TIMEOUT_MS = 90000; // 90 seconds

/**
 * Returns the Set of assignment IDs that are currently being processed
 * (i.e. user changes were submitted within the last 90 seconds).
 */
export const getProcessingAssignments = (): Set<string> => {
  try {
    const stored = sessionStorage.getItem(PROCESSING_ASSIGNMENTS_KEY);
    if (!stored) return new Set();

    const entries: Record<string, { timestamp: number }> = JSON.parse(stored);
    const now = Date.now();
    const validIds = new Set<string>();

    Object.entries(entries).forEach(([id, data]: [string, { timestamp: number }]) => {
      if (now - data.timestamp < PROCESSING_TIMEOUT_MS) {
        validIds.add(id);
      }
    });

    return validIds;
  } catch {
    return new Set();
  }
};

/**
 * Load active assignments (paginated, filtered, sorted)
 * - Fetches all pages of active assignments (server-side filtered)
 * - Filters: not past due
 * - Sorts: by dueDate ascending, then by updatedAt descending
 * - Limits to 4 results
 */
export const loadActiveAssignments = async (
  tenantName: string,
  limit: number = 4
): Promise<AssignmentData[]> => {
  const assignmentsClient = getAssignmentsClient();

  // Fetch first page to get total pages count (using active filter to reduce data load)
  const firstPageResult = await assignmentsClient.assignments(tenantName, {
    page: 1,
    limit: 0,
    filter: "active",
  });

  if (firstPageResult.error) {
    throw firstPageResult.error;
  }

  const firstPage = firstPageResult.data;

  if (!firstPage?.assignments) {
    return [];
  }

  const totalPages = firstPage.totalPages || 1;

  // Fetch first page
  const firstPageAssignments = firstPage.assignments || [];

  // Fetch the rest of the pages starting from page 2
  const remainingPages =
    totalPages > 1
      ? await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) =>
            assignmentsClient
              .assignments(tenantName, { page: i + 2, limit: 0, filter: "active" })
              .then(res => res.data?.assignments || [])
          )
        )
      : [];

  // Combine first page with the rest of the pages
  const allAssignments = [firstPageAssignments, ...remainingPages].flat();

  // Filter: not past due (active filter is already applied server-side)
  const processedAssignments = (allAssignments || [])
    .filter(assignment => {
      // Exclude if due date is in the past
      const dueDateAt = assignment.dueDateAt;
      const isPastDue =
        dueDateAt && dueDateAt !== "0001-01-01T00:00:00Z" && new Date(dueDateAt) < new Date();

      return !isPastDue;
    })
    .sort((a, b) => {
      // Sort by dueDateAt ascending
      const dueDateA =
        a.dueDateAt && a.dueDateAt !== "0001-01-01T00:00:00Z" ? new Date(a.dueDateAt) : null;
      const dueDateB =
        b.dueDateAt && b.dueDateAt !== "0001-01-01T00:00:00Z" ? new Date(b.dueDateAt) : null;

      // Both have due dates - sort ascending
      if (dueDateA && dueDateB) return dueDateA.getTime() - dueDateB.getTime();
      // Only A has due date - A comes first
      if (dueDateA) return -1;
      // Only B has due date - B comes first
      if (dueDateB) return 1;
      // Neither has due date - sort by updatedAt descending
      return new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime();
    })
    .slice(0, limit);

  return processedAssignments;
};

/**
 * Load all assignment policies across all pages (unfiltered).
 * The SVC endpoint sends 100 records per page; fetches all pages in parallel.
 */
export const loadAllAssignmentPolicies = async (
  tenantName: string
): Promise<AssignmentPolicyData[]> => {
  const assignmentsClient = getAssignmentsClient();

  const firstPageResult = await assignmentsClient.assignments(tenantName, { page: 1, limit: 0 });

  if (firstPageResult.error) {
    throw firstPageResult.error;
  }

  const firstPage = firstPageResult.data;
  if (!firstPage?.assignments) return [];

  const totalPages = firstPage.totalPages || 1;
  const firstPageAssignments: AssignmentPolicyData[] = firstPage.assignments || [];

  const remainingPages: AssignmentPolicyData[][] =
    totalPages > 1
      ? await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) =>
            assignmentsClient
              .assignments(tenantName, { page: i + 2, limit: 0 })
              .then(res => (res.data?.assignments as AssignmentPolicyData[]) || [])
          )
        )
      : [];

  return [firstPageAssignments, ...remainingPages].flat();
};

// ─── Phase 2: Admin CRUD functions ───────────────────────────────────────────

/** Mark an assignment as processing (stores timestamp in sessionStorage). */
export const setProcessingAssignment = (id: string): void => {
  try {
    const stored = sessionStorage.getItem(PROCESSING_ASSIGNMENTS_KEY);
    const entries: Record<string, { timestamp: number }> = stored ? JSON.parse(stored) : {};
    entries[id] = { timestamp: Date.now() };
    sessionStorage.setItem(PROCESSING_ASSIGNMENTS_KEY, JSON.stringify(entries));
  } catch {
    // ignore
  }
};

/** Returns true if the given assignment ID is still within its processing window. */
export const isAssignmentProcessing = (id: string | undefined): boolean => {
  if (!id) return false;
  return getProcessingAssignments().has(id);
};

/** Fetch all assignment policies (simple Rails endpoint). */
export const loadAssignmentPoliciesAsync = async (): Promise<AssignmentPolicyData[]> => {
  const { data } = await getRailsClient().get<{ assignmentPolicies?: AssignmentPolicyData[] }>(
    API_ASSIGNMENT_POLICIES_PATH
  );
  return data.assignmentPolicies ?? [];
};

/** Fetch a single assignment policy by ID. */
export const loadAssignmentPolicyByIdAsync = async (id: string): Promise<AssignmentPolicyData> => {
  const { data } = await getRailsClient().get<{
    assignmentPolicy?: AssignmentPolicyData;
    id?: string;
    name?: string;
    roleId?: string;
    duration?: number;
    dueDateAt?: number;
    activated?: boolean;
    description?: string;
    notificationsFrequency?: string;
  }>(`${API_ASSIGNMENT_POLICIES_PATH}/${id}`);
  return (data.assignmentPolicy ?? data) as AssignmentPolicyData;
};

/** Create a new assignment policy. */
export const createAssignmentAsync = async (
  payload: CreateAssignmentPayload
): Promise<{ assignmentPolicy?: { id?: string } }> => {
  const { data } = await getRailsClient().post<{ assignmentPolicy?: { id?: string } }>(
    API_ASSIGNMENT_POLICIES_PATH,
    payload
  );
  return data;
};

/** Update an existing assignment policy. */
export const updateAssignmentPolicyAsync = async (
  id: string,
  payload: Partial<CreateAssignmentPayload>
): Promise<AssignmentPolicyData> => {
  const { data } = await getRailsClient().put<{ assignmentPolicy?: AssignmentPolicyData }>(
    `${API_ASSIGNMENT_POLICIES_PATH}/${id}`,
    payload
  );
  return data.assignmentPolicy ?? {};
};

/** Delete an assignment policy. */
export const deleteAssignmentPolicyAsync = async (id: string): Promise<void> => {
  await getRailsClient().delete(`${API_ASSIGNMENT_POLICIES_PATH}/${id}`);
};

/** Fetch all users assigned to a policy. */
export const loadAssignedUsersAsync = async (policyId: string): Promise<AssignedUser[]> => {
  const { data } = await getRailsClient().get<{
    totalAssignments?: AssignedUser[];
    assignments?: AssignedUser[];
  }>(`${API_ASSIGNMENT_POLICIES_PATH}/${policyId}/assignments`);
  return data.totalAssignments ?? data.assignments ?? [];
};

/** Delete users from an assignment policy by assignment IDs. */
export const deleteAssignmentUsersAsync = async (
  policyId: string,
  assignmentIds: string[]
): Promise<void> => {
  await getRailsClient().delete(`${apiAssignmentPath(policyId)}/${policyId}`, {
    params: { ids: assignmentIds },
  });
};

/** Fetch assignment policy rules for a policy. */
export const loadAssignmentPolicyRulesAsync = async (
  policyId: string
): Promise<AssignmentPolicyRule[]> => {
  const { data } = await getRailsClient().get<{
    assignmentPolicyRules?: AssignmentPolicyRule[];
  }>(`${API_V1}/assignment_policies/${policyId}/assignment_policy_rules`);
  return data.assignmentPolicyRules ?? [];
};

/** Create an assignment policy rule (e.g. new hire rule). */
export const createAssignmentPolicyRuleAsync = async (
  policyId: string,
  payload: NewHireRulePayload
): Promise<AssignmentPolicyRule> => {
  const { data } = await getRailsClient().post<AssignmentPolicyRule>(
    `${API_V1}/assignment_policies/${policyId}/assignment_policy_rules`,
    payload
  );
  return data;
};

/** Delete assignment policy rules by IDs. */
export const deleteAssignmentPolicyRuleAsync = async (
  policyId: string,
  ruleIds: string[]
): Promise<void> => {
  await getRailsClient().delete(
    `${API_V1}/assignment_policies/${policyId}/assignment_policy_rules`,
    { params: { ids: ruleIds } }
  );
};

/** Fetch all paths/roles (defaultPaths, paths, quests). */
export const loadPathsAsync = async (
  params: Record<string, unknown> = {}
): Promise<PathsApiResponse> => {
  const { data } = await getRailsClient().get<PathsApiResponse>(`${API_V1}/roles`, { params });
  return data;
};

/** Fetch a single path/role by ID. */
export const loadPathByIdAsync = async (id: string): Promise<{ role?: Path } & Path> => {
  const { data } = await getRailsClient().get<{ role?: Path } & Path>(`${API_V1}/roles/${id}`);
  return data;
};

/** Fetch level definitions. */
export const loadLevelsAsync = async (): Promise<Level[]> => {
  const { data } = await getRailsClient().get<{ courses?: Level[]; levels?: Level[] } | Level[]>(
    `${API_V1}/levels`
  );
  if (Array.isArray(data)) return data;
  return data.courses ?? data.levels ?? [];
};

/** Fetch level user data (directoryUuids grouped by level rankName). */
export const loadLevelDataAsync = async (): Promise<Record<string, string[]>> => {
  const { data } = await getRailsClient().get<
    { levels?: Record<string, string[]> } & Record<string, string[]>
  >(`${ATLAS_API_V1}/stats/levels`);
  // API may return { levels: {...} } (nested) or a flat object — handle both
  return data.levels ?? data ?? {};
};

/**
 * Find the best matching Path for an assignment policy.
 * First tries exact ID match; if not found, loads the path and checks its versions.
 */
export const findMatchingPath = async (
  assignmentPolicy: { roleId?: string },
  availablePaths: Path[]
): Promise<Path | null> => {
  let selectedPath = availablePaths.find(p => p.id === assignmentPolicy.roleId) ?? null;

  if (!selectedPath && assignmentPolicy.roleId) {
    try {
      const fullPathData = await loadPathByIdAsync(assignmentPolicy.roleId);
      const roleData = fullPathData.role ?? fullPathData;

      if (roleData.versions && Array.isArray(roleData.versions)) {
        const versionMatch = roleData.versions.find(v => v.id === assignmentPolicy.roleId);
        if (versionMatch) selectedPath = versionMatch;
      }
    } catch {
      // path not found — leave as null
    }
  }

  return selectedPath;
};

/**
 * Transform a raw assignment policy + assigned users into FormValues.
 */
export const transformApiDataToFormValues = async (
  assignmentPolicy: AssignmentPolicyData & {
    roleId?: string;
    duration?: number;
    dueDateAt?: number;
    description?: string;
    notificationsFrequency?: string;
  },
  assignedUsers: AssignedUser[],
  availablePaths: Path[],
  availableUsers: DirectoryUserShape[]
): Promise<FormValues> => {
  const selectedPath = await findMatchingPath(assignmentPolicy, availablePaths);

  const settings: FormSettings = {
    name: assignmentPolicy.name,
    description: assignmentPolicy.description,
    activated: assignmentPolicy.activated,
    // Rails returns lowercase (e.g. "none", "weekly"). Normalise so the radio
    // buttons in AssignmentSettings (which use lowercase option values) always
    // match regardless of what the API sends.
    sendReminders: normalizeNotificationsFrequency(assignmentPolicy.notificationsFrequency),
  };

  if (assignmentPolicy.dueDateAt && assignmentPolicy.dueDateAt > 0) {
    const endDate = new Date(assignmentPolicy.dueDateAt * 1000);
    settings.endDate = endDate;
    settings.dates = { dynamic: false };
  } else if (assignmentPolicy.duration && assignmentPolicy.duration > 0) {
    settings.dates = {
      dynamic: true,
      dueAfter: { days: assignmentPolicy.duration },
    };
  }

  const users: StoredUser[] = (assignedUsers ?? [])
    .map((assignment): StoredUser | null => {
      const apiUser = assignment.user;
      if (!apiUser?.email) return null;
      const emailLower = apiUser.email.toLowerCase();
      const matched = availableUsers.find(u => u.email?.toLowerCase() === emailLower);
      return {
        assignmentId: assignment.id,
        directoryUuid: apiUser.directoryUuid ?? matched?.directoryUuid ?? apiUser.email,
        email: apiUser.email,
        firstName: apiUser.firstName,
        lastName: apiUser.lastName,
      };
    })
    .filter((u): u is StoredUser => u !== null);

  return {
    selectedPath,
    target: { availableUsers, users },
    settings,
  };
};
